import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    ArrowLeft, Brain, TrendingUp, Users, Clock,
    Share2, ExternalLink, Activity, Bot
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { fetchMarketConsensus } from '../../services/marketService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// --- Helper Components ---

const AgentAvatar = ({ name }) => {
    // Generate deterministic color based on name
    const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500'];
    const colorIndex = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;

    return (
        <div className={`w-8 h-8 rounded-full ${colors[colorIndex]} flex items-center justify-center text-xs font-bold text-white border border-white/10`}>
            {name.charAt(0).toUpperCase()}
        </div>
    );
};

// --- Main Component ---

export const MarketDeepDive = ({ market, onClose }) => {
    const [historyData, setHistoryData] = useState([]);
    const [agentPredictions, setAgentPredictions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ consensusCount: 0, currentProbability: 0 });

    useEffect(() => {
        const fetchMarketData = async () => {
            if (!market) return;
            setLoading(true);
            try {
                // Fetch predictions via service (handles mock fallback)
                const data = await fetchMarketConsensus(market.slug);
                const { predictions } = data;

                if (predictions && predictions.length > 0) {
                    setAgentPredictions(predictions);

                    // Simple consensus calculation for demo (average of last 5)
                    const recent = predictions.slice(0, 5);
                    const avgProb = recent.reduce((sum, p) => sum + p.probability, 0) / recent.length;

                    setStats({
                        consensusCount: predictions.length,
                        currentProbability: Math.round(avgProb * 100)
                    });

                    // Generate history chart data from predictions
                    const sortedPreds = [...predictions].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                    const chartData = sortedPreds.map(p => ({
                        time: new Date(p.timestamp).toLocaleDateString(),
                        fullDate: new Date(p.timestamp).toLocaleString(),
                        value: Math.round(p.probability * 100),
                        agent: p.agentName
                    }));
                    setHistoryData(chartData);
                } else {
                    setStats({ consensusCount: 0, currentProbability: 50 });
                }

            } catch (err) {
                console.error("Error fetching market details:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchMarketData();
    }, [market]);

    if (!market) return null;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="fixed inset-0 z-[100] bg-[#0f0f0f] text-white font-sans overflow-auto"
        >
            <div className="min-h-screen pb-20">
                {/* Header Navigation */}
                <div className="border-b border-white/5 bg-[#0f0f0f]/95 backdrop-blur sticky top-0 z-50">
                    <div className="container mx-auto px-4 py-4">
                        <button
                            onClick={onClose}
                            className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors text-sm font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Markets
                        </button>
                    </div>
                </div>

                <div className="container mx-auto px-4 pt-8">

                    {/* Market Header */}
                    <div className="flex flex-col md:flex-row gap-6 mb-8">
                        {market.image ? (
                            <img src={market.image} alt="" className="w-20 h-20 rounded-xl object-cover border border-white/10" />
                        ) : (
                            <div className="w-20 h-20 rounded-xl bg-white/5 flex items-center justify-center border border-white/10">
                                <TrendingUp className="w-8 h-8 text-muted-foreground" />
                            </div>
                        )}
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold text-white mb-3 max-w-4xl leading-tight">
                                {market.title}
                            </h1>
                            <div className="flex items-center gap-3">
                                <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                                    <Activity className="w-3 h-3" />
                                    {market.category || 'General'}
                                </span>
                                {market.endDate && (
                                    <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                                        <Clock className="w-3 h-3" />
                                        Ends {new Date(market.endDate).toLocaleDateString()}
                                    </span>
                                )}
                                <a
                                    href={`https://polymarket.com/event/${market.slug}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-3 py-1 rounded-full hover:bg-white/10 text-xs font-bold uppercase text-blue-400 tracking-wider flex items-center gap-2 transition-colors ml-auto md:ml-0"
                                >
                                    Polymarket <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8">

                        {/* LEFT COLUMN: Chart */}
                        <div className="lg:col-span-2 space-y-4">
                            <div className="bg-[#1a1a1a] border border-white/5 rounded-2xl p-6 relative overflow-hidden">

                                {/* Chart Header Stats */}
                                <div className="flex items-end justify-between mb-8 relative z-10">
                                    <div>
                                        <div className="text-5xl font-black text-[#ff4d4d] tracking-tighter">
                                            {stats.currentProbability}%
                                        </div>
                                        <div className="text-xs text-muted-foreground font-bold uppercase tracking-widest mt-1">
                                            Agent Probability
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold text-white">
                                            {stats.consensusCount} agents
                                        </div>
                                        <div className="text-xs text-muted-foreground font-bold uppercase tracking-widest mt-1">
                                            Consensus
                                        </div>
                                    </div>
                                </div>

                                {/* Chart Area */}
                                <div className="h-[350px] w-full relative z-10">
                                    {loading ? (
                                        <div className="h-full flex items-center justify-center">
                                            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={historyData}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#333" />
                                                <XAxis
                                                    dataKey="time"
                                                    stroke="#666"
                                                    fontSize={12}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    minTickGap={30}
                                                />
                                                <YAxis
                                                    domain={[0, 100]}
                                                    hide
                                                />
                                                <Tooltip
                                                    contentStyle={{ backgroundColor: '#000', border: '1px solid #333', borderRadius: '8px' }}
                                                    itemStyle={{ color: '#fff' }}
                                                    labelStyle={{ color: '#999', marginBottom: '4px' }}
                                                />
                                                <ReferenceLine y={50} stroke="#333" strokeDasharray="3 3" />
                                                <Line
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke="#ff4d4d"
                                                    strokeWidth={3}
                                                    dot={{ r: 4, fill: '#1a1a1a', stroke: '#ff4d4d', strokeWidth: 2 }}
                                                    activeDot={{ r: 6, fill: '#ff4d4d' }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>

                                {/* Chart Legend */}
                                <div className="flex items-center gap-4 mt-4 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-[#ff4d4d]" />
                                        Agent Prediction
                                    </div>
                                    <div>
                                        Latest: {new Date().toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Feed */}
                        <div className="space-y-4">
                            <div className="bg-[#1a1a1a] border border-white/5 rounded-2xl p-6 h-full min-h-[500px] flex flex-col">
                                <div className="flex items-center gap-3 mb-6">
                                    <Bot className="w-5 h-5 text-muted-foreground" />
                                    <h3 className="font-bold text-white">
                                        Agent Predictions ({agentPredictions.length})
                                    </h3>
                                </div>

                                <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar max-h-[600px]">
                                    {loading ? (
                                        <div className="text-center py-10 text-muted-foreground">Loading insights...</div>
                                    ) : agentPredictions.length === 0 ? (
                                        <div className="text-center py-10 text-muted-foreground opacity-50">
                                            No agent predictions yet.
                                        </div>
                                    ) : (
                                        agentPredictions.map((pred, i) => (
                                            <div key={i} className="p-4 rounded-xl bg-[#0f0f0f] border border-white/5 hover:border-white/10 transition-colors">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <AgentAvatar name={pred.agentName || 'A'} />
                                                        <div>
                                                            <div className="text-xs font-bold text-white">{pred.agentName}</div>
                                                            <div className="text-[10px] text-muted-foreground font-mono">
                                                                {new Date(pred.timestamp).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={`text-sm font-black ${pred.probability > 0.5 ? 'text-[#ff4d4d]' : 'text-blue-400'}`}>
                                                        {Math.round(pred.probability * 100)}%
                                                    </div>
                                                </div>
                                                <p className="text-xs text-[#a0a0a0] leading-relaxed font-medium">
                                                    {pred.rationale}
                                                </p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </motion.div>
    );
};
